import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useNavigate, useParams } from 'react-router'
import { api } from '../api/client'
import type { Erfassung } from '../api/typen'
import AppRahmen from '../komponenten/AppRahmen'

/**
 * Uebersicht einer Erfassung: alle Versorgungspunkte des Standorts, dazu die Wege zu
 * Bueroansicht, Stueckliste und Verifizierungsreport. Sonderfaelle werden am Versorgungspunkt
 * erfasst, nicht hier.
 */
export default function ErfassungUebersicht() {
  const { erfassungId } = useParams()
  const navigiere = useNavigate()
  const abfragen = useQueryClient()

  const loeschen = useMutation({
    mutationFn: () => api.loeschen(`/api/erfassungen/${erfassungId}`),
    onSuccess: () => navigiere('/'),
  })

  const erfassung = useQuery({
    queryKey: ['erfassung', erfassungId],
    queryFn: () => api.get<Erfassung>(`/api/erfassungen/${erfassungId}`),
  })

  const punkte = erfassung.data?.versorgungspunkte ?? []

  return (
    <AppRahmen
      titel={erfassung.data?.standort ?? 'Erfassung'}
      unter="Versorgungspunkte"
      zurueck="/"
      pfad={
        erfassung.data
          ? {
              erfassungId: erfassung.data.id,
              standort: erfassung.data.standort,
              versorgungspunktId: null,
              versorgungspunkt: null,
              schrankId: null,
              schrank: null,
            }
          : undefined
      }
      ebene="erfassung"
      menue={[
        {
          beschriftung: 'Ergebnis und Stückliste',
          beiKlick: () => navigiere(`/erfassungen/${erfassungId}/ergebnis`),
        },
        { beschriftung: 'Prüfung im Büro', beiKlick: () => navigiere(`/erfassungen/${erfassungId}/buero`) },
        {
          beschriftung: 'Erfassung umbenennen',
          eingabe: {
            feld: 'Standort',
            wert: erfassung.data?.standort ?? '',
            hinweis:
              'Die Erfassung wird dem eingegebenen Standort zugeordnet; die Fotos ziehen mit um. Andere Erfassungen am bisherigen Standort bleiben unverändert.',
            speichern: async (standort) => {
              await api.patch(`/api/erfassungen/${erfassungId}`, { standort })
              // Der Standort steht auch in Pfadleisten und Listen anderer Bildschirme.
              void abfragen.invalidateQueries()
            },
          },
        },
        {
          beschriftung: 'Erfassung löschen',
          gefahr: true,
          bestaetigung: {
            frage:
              'Alle Versorgungspunkte, Schränke, Fächer und Fotos dieser Erfassung werden entfernt. Das lässt sich nicht rückgängig machen.',
            bestaetigungstext: 'Endgültig löschen',
          },
          beiKlick: () => loeschen.mutate(),
        },
      ]}
      fuss={
        <button
          className="btn btn-primaer"
          onClick={() => navigiere(`/erfassungen/${erfassungId}/versorgungspunkt-neu`)}
        >
          Versorgungspunkt anlegen
        </button>
      }
    >
      {erfassung.isPending && <div className="spinner" />}

      {punkte.length === 0 && (
        <p className="sek">
          Noch kein Versorgungspunkt angelegt. Ein Versorgungspunkt ist ein räumlich zusammenhängender
          Versorgungsbereich – zwei Räume links und rechts eines Flurs sind zwei Punkte.
        </p>
      )}

      {punkte.map((punkt) => {
        const erfassteFaecher = punkt.schraenke.reduce(
          (summe, schrank) => summe + schrank.anzahlFaecherErfasst,
          0,
        )
        const sollFaecher = punkt.schraenke.reduce((summe, schrank) => summe + schrank.anzahlFaecherSoll, 0)
        return (
          <button
            key={punkt.id}
            className="karte"
            onClick={() => navigiere(`/versorgungspunkte/${punkt.id}`)}
          >
            <div className="zeile">
              <b>{punkt.bezeichnung}</b>
              <span className="st st-arbeit">{punkt.vpCode}</span>
            </div>
            <span className="sek">
              {punkt.schraenke.length === 1 ? '1 Schrank' : `${punkt.schraenke.length} Schränke`}
              {punkt.anzahlSchraenkeSoll ? ` von ${punkt.anzahlSchraenkeSoll} geplanten` : ''} · Fächer:{' '}
              {erfassteFaecher} aufgenommen, {sollFaecher} konfiguriert
            </span>
            {punkt.notiz && <span className="sek">{punkt.notiz}</span>}
          </button>
        )
      })}
    </AppRahmen>
  )
}
